import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { Refresh as RefreshIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import moment from "moment";
import { useIntl } from "react-intl";
import { VisualizationSpec } from "vega-embed";

// Components
import CardMCVegaChart from "../MeChart/components/CardMCVegaChart";
import ModMeGenAiUsage from "./common/ModMeGenAiUsage";
import ModAiUsageBreakdown from "./common/ModAiUsageBreakdown";
import AiRatePriceCell from "./common/AiRatePriceCell";
import CardAiRateCard from "./common/CardAiRateCard";
import AiUsageModelLabel from "./common/AiUsageModelLabel";
import CardAiUsageProjects from "./common/CardAiUsageProjects";
import FfmpegUsageSummary from "./common/FfmpegUsageSummary";
import { FfmpegAllocationCost, FfmpegAllocationTime } from "./common/FfmpegAllocationValue";
import { formatTaskCost, isUsageError, knownTaskValue } from "./common/ffmpegUsage";
import { NO_THREAD_PROJECT, projectKeyOf } from "./common/aiUsageProjects";

// Interface / Types
import { State, Event, SetProp } from "./MeGenInterface";
import AiUsagePagination, { AI_USAGE_CHART_SIZE, pageOf } from "./common/AiUsagePagination";
import { AiUsageRecordRow, AiUsageThreadDetail } from "./types/MeGenTypes";
import {
  AI_USAGE_GROUPS,
  AI_USAGE_METRICS,
  AiUsageAggregate,
  AiUsageGroupKey,
  AiUsageMetricKey,
  aggregateRecords,
  dayOf,
  formatMetric,
  grandTotals,
  groupKeyOf,
  metricLabelWithUnit,
  toLocalDateTime,
  CLOUD_BUCKETS,
  cloudBucketOf,
  serviceLabelOf,
  formatMetricValue,
  formatMoney,
} from "./common/aiUsagePivot";
import { CostResult, PriceLineInfo, buildPriceLineInfos, makeRecordCostFn, makeRecordCostInfoFn, priceLineKeyOf } from "./common/aiModelCost";
import { usageModelKeyOf, usageModelOf } from "./common/aiUsageModels";
import { buildStackHover } from "./common/aiUsageCharts";
import { isRateOverridden, variantLabelOf } from "./common/aiModelRates";
import { useAiModelRates } from "./common/useAiModelRates";

// index ของแท็บ (ราคา Model แสดงเสมอแม้ไม่มีข้อมูลในช่วงเวลา)
const TAB = { daily: 0, thread: 1, model: 2, records: 3, rates: 4, project: 5 } as const;

// ─── Palette ─────────────────────────────────────────────────────────────────
// 8 สีแรกผ่าน validate_palette.js (CVD ΔE 9.1, slot order คงที่) — ชุดขยายรองรับการแยกทุกกลุ่ม
// โดยไม่ยุบเป็น "อื่นๆ" (ตามที่ผู้ใช้ขอ) เกิน 20 กลุ่มค่อยวนซ้ำ
const SERIES_COLORS = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948",
  "#17b0c4", "#9a6324", "#c231c2", "#7f8c1f", "#155a9e", "#7c1f4f", "#b28dd9", "#0d5c63",
  "#94b8e8", "#8c564b", "#c9b03c", "#666666",
];
const FALLBACK_COLOR = "#9ca3af";
const SINGLE_HUE = "#2a78d6"; // กราฟ magnitude หมวดเดียว (top-N)

const AXIS_CONFIG = {
  labelColor: "#64748b",
  titleColor: "#334155",
  gridColor: "#f1f5f9",
  domainColor: "#e2e8f0",
  tickColor: "#e2e8f0",
};

const VEGA_CONFIG = {
  axis: AXIS_CONFIG,
  legend: { labelColor: "#334155", titleColor: "#334155" },
  view: { stroke: null as any },
  background: "transparent",
};

// ─── Styled ──────────────────────────────────────────────────────────────────

// pane ของเมนู admin ไม่ scroll ให้ — dashboard ต้อง scroll เองภายใน (feedback: เลื่อนลงล่างไม่ได้)
const Container = styled(Box)({
  height: "100%",
  overflowY: "auto",
  padding: "1rem 1.25rem 4rem",
});

const Inner = styled(Box)({ maxWidth: "1280px", margin: "0 auto" });

const SectionCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  padding: "1.25rem 1.5rem",
  marginBottom: "1rem",
});

const SectionTitle = styled(Typography)({
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "0.75rem",
});

const thTd: React.CSSProperties = {
  padding: "7px 12px",
  fontSize: "0.78rem",
  borderBottom: "1px solid #f1f5f9",
  textAlign: "left",
  whiteSpace: "nowrap",
};
const thStyle: React.CSSProperties = { ...thTd, fontSize: "0.7rem", color: "#64748b", backgroundColor: "#f8fafc", textTransform: "uppercase", letterSpacing: "0.03em" };
const numTd: React.CSSProperties = { ...thTd, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// ─── Component ───────────────────────────────────────────────────────────────

type BreakdownState = {
  title: string;
  subtitle?: string;
  records: AiUsageRecordRow[];
} | null;

type CardMeGenCostMainProps = {
  MeGenSequence: State["MeGenSequence"];
  onEvent: (e: Event) => void;
  setProp?: SetProp;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
};

const CardMeGenCostMain: React.FC<CardMeGenCostMainProps> = (props) => {
  const intl = useIntl();
  const [startDate, setStartDate] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [activePreset, setActivePreset] = useState<number | null>(30);

  const [metric, setMetric] = useState<AiUsageMetricKey>("calls");
  const [groupBy, setGroupBy] = useState<AiUsageGroupKey>("service");
  const [barMode, setBarMode] = useState<"stacked" | "grouped">("stacked");
  const [activeTab, setActiveTab] = useState(0);

  // Records tab filters
  const [recordSearch, setRecordSearch] = useState("");
  const [threadSearch, setThreadSearch] = useState("");
  const [recordPage, setRecordPage] = useState(0);
  const [threadPage, setThreadPage] = useState(0);
  const [threadChartPage, setThreadChartPage] = useState(0);
  const [modelChartPage, setModelChartPage] = useState(0);
  const [modelPage, setModelPage] = useState(0);
  const [videoLimit, setVideoLimit] = useState(0);

  // Global filters (กรอง 2 ชั้น: รายคน + cloud — ใช้กับทุกอย่างในหน้า)
  const [userFilter, setUserFilter] = useState<string>("all");
  const [cloudFilter, setCloudFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");

  // Modals
  const [usageThreadId, setUsageThreadId] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownState>(null);

  const records = props.MeGenSequence?.aiUsageRecords || [];
  const loading = props.MeGenSequence?.aiUsageLoading;
  const loadError = props.MeGenSequence?.aiUsageLoadError;
  const appliedRange = props.MeGenSequence?.aiUsageAppliedRange;
  const progress = props.MeGenSequence?.aiUsageProgress;
  const titleProgress = props.MeGenSequence?.aiUsageTitleProgress;
  const threadTitles = props.MeGenSequence?.aiUsageThreadTitles || {};
  const projectIndex = props.MeGenSequence?.aiUsageProjectIndex;
  const projectProgress = props.MeGenSequence?.aiUsageProjectProgress;
  const projectError = props.MeGenSequence?.aiUsageProjectError;
  const projectFilterUnverified = projectFilter !== "all" && (!projectIndex || !!projectProgress?.loading || !!projectError || !!projectIndex.failedProjects || !!projectIndex.conflictedThreads);

  // user_id -> ชื่อ: base จาก allOaUsersList แล้วทับด้วย user_name ที่ backend แนบมากับ records
  // (user_directory ฝั่ง server — ครอบคลุม id ที่ไม่อยู่ในรายชื่อ OA ด้วย)
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    (props.MeGenSequence?.allOaUsersList || []).forEach((u) => {
      const userId = String(u.user);
      const displayName = u.user_profile?.fullname || u.user_profile?.username || `User ${userId}`;
      if (userId) map[userId] = displayName;
    });
    records.forEach((r) => {
      if (r.user_id && r.user_name) map[r.user_id] = r.user_name;
    });
    return map;
  }, [props.MeGenSequence?.allOaUsersList, records]);

  const nameOf = (userId: string | null | undefined): string =>
    (userId && userNameMap[userId]) || userId || "-";

  const titleOf = (threadId: string | null | undefined): string =>
    (threadId && threadTitles[threadId]) || "";

  // ── ราคาต่อ model: price book + ราคาที่แก้ในแท็บ ราคา Model (localStorage) — ใช้ร่วมกับ modal run ──
  const rates = useAiModelRates();
  const costOf = useMemo(() => makeRecordCostFn(rates.overrides), [rates.overrides]);
  const costInfoOf = useMemo(() => makeRecordCostInfoFn(rates.overrides), [rates.overrides]);

  // All totals/tabs/drilldowns share the same user/cloud/project-filtered records.
  const data = useMemo(
    () =>
      records.filter(
        (r) =>
          (userFilter === "all" || r.user_id === userFilter) &&
          (cloudFilter === "all" || cloudBucketOf(r.provider) === cloudFilter) &&
          (projectFilter === "all" || projectKeyOf(r, projectIndex) === projectFilter)
      ),
    [records, userFilter, cloudFilter, projectFilter, projectIndex]
  );
  const projectOptions = useMemo(() => {
    const names = new Map(projectIndex?.projects.map((project) => [project.id, project.name]));
    return Array.from(new Set(records.map((row) => projectKeyOf(row, projectIndex)))).map((key) => {
      const id = key.startsWith("project:") ? Number(key.slice(8)) : null;
      return { key, label: id !== null ? `${names.get(id) || intl.formatMessage({ id: "megen.cost.projectName" })} (#${id})` : intl.formatMessage({ id: key === NO_THREAD_PROJECT ? "megen.cost.projectNoThread" : "megen.cost.projectUnmatched" }) };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [records, projectIndex, intl]);
  const selectedThreadRecords = useMemo(() => usageThreadId ? data.filter((record) => record.thread_id === usageThreadId) : undefined, [data, usageThreadId]);

  // ตัวเลือกรายคน: จาก records ทั้งช่วงเวลา (ก่อน filter) เรียงตามปริมาณใช้
  const userOptions = useMemo(
    () =>
      aggregateRecords(records.filter((r) => r.user_id), (r) => r.user_id as string)
        .sort((a, b) => b.calls - a.calls)
        .map((a) => a.key),
    [records]
  );

  // ตัวเลือก cloud: โชว์เฉพาะ bucket ที่มีข้อมูลจริงในช่วงเวลา
  const cloudOptions = useMemo(() => {
    const present = new Set(records.map((r) => cloudBucketOf(r.provider)));
    const opts = CLOUD_BUCKETS.filter((b) => present.has(b.key)).map((b) => ({ key: b.key, label: b.label }));
    if (present.has("other")) opts.push({ key: "other", label: "อื่นๆ" });
    return opts;
  }, [records]);

  const fetchRecords = (start: string, end: string) => {
    props.onEvent({
      message: "HandleGetAiUsageRecords",
      params: { startDate: start, endDate: end },
    });
  };

  // โหลดครั้งแรกเมื่อเปิดเมนู
  useEffect(() => {
    fetchRecords(startDate, endDate);
    return () => props.onEvent({ message: "HandleCancelAiUsageRecords", params: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecordPage(0); setThreadPage(0); setThreadChartPage(0); setModelChartPage(0); setModelPage(0);
    setBreakdown(null); setUsageThreadId(null);
  }, [records, userFilter, cloudFilter, projectFilter, projectIndex]);
  useEffect(() => { setRecordPage(0); }, [recordSearch]);
  useEffect(() => { setThreadPage(0); }, [threadSearch]);
  useEffect(() => { setThreadChartPage(0); setModelChartPage(0); }, [metric, videoLimit, costOf]);

  const handlePreset = (days: number) => {
    const start = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
    const end = moment().format("YYYY-MM-DD");
    setStartDate(start);
    setEndDate(end);
    setActivePreset(days);
    fetchRecords(start, end);
  };

  const handleFetchAiUsageDetail = (
    threadId: string,
    onSuccess: (detail: AiUsageThreadDetail) => void,
    onError: (message: string) => void
  ) => {
    props.onEvent({
      message: "HandleGetAiUsageThreadDetail",
      params: { threadId, onSuccess, onError },
    });
  };

  // ─── Pivots (คำนวณจาก data ที่ผ่าน filter ชุดเดียวกันทุกแท็บ) ─────────────
  const totals = useMemo(() => grandTotals(data, costOf), [data, costOf]);

  // ทุกกลุ่มเรียงตาม "calls" จาก records ก่อน filter — ไม่ยุบ "อื่นๆ" แล้ว (แยกออกมาทุกกลุ่ม)
  // สลับ metric/filter แล้วสี/ลำดับกลุ่มต้องไม่เปลี่ยน (color follows entity)
  const allGroups = useMemo(() => {
    const aggs = aggregateRecords(records, (r) => groupKeyOf(r, groupBy));
    return aggs.sort((a, b) => b.calls - a.calls).map((a) => a.key);
  }, [records, groupBy]);

  // ป้ายแสดงผลของกลุ่ม: groupBy=user แปลง id→ชื่อ (เติม id ต่อท้ายเมื่อชื่อซ้ำกัน)
  const groupDisplay = useMemo(() => {
    const display: Record<string, string> = {};
    const names = allGroups.map((g) =>
      groupBy === "user" ? nameOf(g) : groupBy === "service" ? serviceLabelOf(g) : g
    );
    const counts = new Map<string, number>();
    names.forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
    const dupe = new Set(names.filter((name) => (counts.get(name) || 0) > 1));
    allGroups.forEach((g, i) => {
      display[g] = dupe.has(names[i]) ? `${names[i]} (${g})` : names[i];
    });
    return display;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups, groupBy, userNameMap]);

  // ตารางจับคู่สีถาวร (จาก records ก่อน filter) — filter แล้วกลุ่มที่เหลือต้องสีเดิมเสมอ
  const stableGroupColor = useMemo(() => {
    const map: Record<string, string> = {};
    allGroups.forEach((g, i) => {
      map[groupDisplay[g]] = SERIES_COLORS[i % SERIES_COLORS.length];
    });
    return map;
  }, [allGroups, groupDisplay]);

  // ลำดับ slot คงที่ ใช้เรียง legend / แท่งเทียบข้างกัน
  const groupSlotOrder = useMemo(
    () => allGroups.map((g) => groupDisplay[g]),
    [allGroups, groupDisplay]
  );

  const dailyAggs = useMemo(
    () => activeTab === TAB.daily ? aggregateRecords(data, dayOf, costOf).sort((a, b) => (a.key < b.key ? -1 : 1)) : [],
    [data, costOf, activeTab]
  );

  const threadAggs = useMemo(
    () =>
      activeTab === TAB.thread ? aggregateRecords(data.filter((r) => r.thread_id), (r) => r.thread_id as string, costOf).sort((a, b) =>
        (b.last_used || "") < (a.last_used || "") ? -1 : 1
      ) : [],
    [data, costOf, activeTab]
  );

  const modelAggs = useMemo(
    () =>
      activeTab === TAB.model ? aggregateRecords(data, usageModelKeyOf, costOf).sort((a, b) => b.calls - a.calls) : [],
    [data, costOf, activeTab]
  );

  // "price line" = model × variant (resolution/เสียงที่ผู้ให้บริการคิดเงิน) — แถวของตาราง rate card ในแท็บ ราย Model
  // key ไม่ขึ้นกับ override → แถวไม่สลับตอนพิมพ์ราคา
  const priceLines = useMemo(() => {
    // flags/reasons ของบรรทัดรวมจากทุก record (ไม่ใช่ record แรก) — เช่น "ไม่มีเวลาเครื่อง 3/10 call"
    const infos = buildPriceLineInfos(data, rates.overrides);
    const lines: { agg: AiUsageAggregate; service: string; provider: string; model: string; variant: string; info: PriceLineInfo }[] = [];
    aggregateRecords(data, priceLineKeyOf, costOf)
      .sort((a, b) => b.cost_usd - a.cost_usd || b.calls - a.calls)
      .forEach((agg) => {
        const info = infos.get(agg.key);
        if (!info) return;
        const [service, provider, model, variant] = agg.key.split("|");
        lines.push({ agg, service, provider, model, variant, info });
      });
    return lines;
  }, [data, costOf, rates.overrides]);

  // model ที่ยังไม่ได้ตั้งราคา (เตือนที่การ์ดค่าใช้จ่าย)
  const missingRateModels = useMemo(
    () => Array.from(new Set(priceLines.filter((p) => p.service !== "ffmpeg" && p.info.result.missingRate).map((p) => p.model))),
    [priceLines]
  );

  // จาก records ก่อน filter — เป็นตัวกำหนด domain/สีของ donut (filter แล้วสีกลุ่มเดิมต้องไม่เปลี่ยน)
  const providerAggs = useMemo(
    () => aggregateRecords(records, (r) => r.provider || "unknown").sort((a, b) => b.calls - a.calls),
    [records]
  );
  const allProviders = useMemo(() => providerAggs.map((a) => a.key), [providerAggs]);

  // ─── Vega specs ────────────────────────────────────────────────────────────
  const metricLabel = metricLabelWithUnit(metric); // เช่น "วิดีโอ (วินาที)"
  const valueFormat = metric === "cost_usd" ? ",.4~f" : ",.2~f"; // tooltip vega — เงินต้องเห็นถึง 4 ตำแหน่ง
  const groupLabel = AI_USAGE_GROUPS.find((g) => g.key === groupBy)?.label || groupBy;

  // รายวัน: stacked bar x=วัน, stack ตาม group-by
  // NOTE: pre-aggregate เองแล้ว (1 แถวต่อ วัน×กลุ่ม) — ห้ามใส่ aggregate ใน encoding
  // ไม่งั้น field ประกอบ (dayRaw/groupKey) จะหายจาก datum ตอนคลิก drill-down
  const dailySpec = useMemo((): VisualizationSpec => {
    if (activeTab !== TAB.daily) return {} as VisualizationSpec;
    const dayGroup = aggregateRecords(data, (r) => `${dayOf(r)}|${groupKeyOf(r, groupBy)}`, costOf);
    const rows = dayGroup
      .map((a) => {
        const [day, groupKey] = a.key.split("|");
        return { day, groupKey, group: groupDisplay[groupKey] || groupKey, value: a[metric] as number };
      })
      .filter((r) => r.value > 0) // กลุ่มที่ metric นี้เป็น 0 ไม่ต้องเข้ากราฟ/legend
      .sort((a, b) => (a.day < b.day ? -1 : 1)); // grouped mode ใช้แกน ordinal ตามลำดับ data

    // tooltip แจกแจงทั้งแท่ง: ทุก datum ของวันเดียวกันแชร์ object เดียว (vega-tooltip render object เป็นตาราง)
    const hoverByDay = buildStackHover(rows, metric);

    const values = rows.map((r) => ({
      day: `${r.day}T00:00:00`, // naive datetime → vega ตีความเป็น local (date-only จะโดนตีเป็น UTC)
      dayRaw: r.day,
      dayLabel: moment(r.day).format("D MMM"),
      groupKey: r.groupKey,
      group: r.group,
      value: r.value,
      hover: hoverByDay[r.day],
    }));

    // legend/สี: เฉพาะกลุ่มที่มีข้อมูลจริงหลัง filter แต่สีหยิบจากตารางถาวร → กลุ่มเดิมสีเดิมเสมอ
    const present = new Set(rows.map((r) => r.group));
    const presentGroups = groupSlotOrder.filter((g) => present.has(g));

    return {
      width: "container",
      height: 260,
      config: VEGA_CONFIG,
      data: { values },
      mark: { type: "bar", stroke: "white", strokeWidth: 1, cursor: "pointer" },
      encoding: {
        x:
          barMode === "grouped"
            ? { field: "dayRaw", type: "nominal", sort: null, axis: { title: null, labelAngle: -45 } }
            : { field: "day", type: "temporal", timeUnit: "yearmonthdate", axis: { title: null, format: "%d %b" } },
        ...(barMode === "grouped" ? { xOffset: { field: "group", sort: presentGroups } } : {}),
        y: { field: "value", type: "quantitative", axis: { title: metricLabel } },
        color: {
          field: "group",
          type: "nominal",
          title: groupLabel,
          scale: { domain: presentGroups, range: presentGroups.map((g) => stableGroupColor[g] || FALLBACK_COLOR) },
        },
        tooltip: { field: "hover", type: "nominal" },
      },
    } as VisualizationSpec;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metric, groupBy, groupDisplay, groupSlotOrder, stableGroupColor, barMode, costOf, activeTab]);

  const handleDailyClick = (datum: any) => {
    if (!datum?.dayRaw || !datum?.groupKey) return;
    const subset = data.filter(
      (r) => dayOf(r) === datum.dayRaw && groupKeyOf(r, groupBy) === datum.groupKey
    );
    setBreakdown({
      title: `${datum.dayRaw} · ${datum.group}`,
      subtitle: `ส่วนประกอบของแท่ง ${metricLabel} รายวัน`,
      records: subset,
    });
  };

  const rankedThreads = useMemo(() => {
    const ranked = [...threadAggs].sort((a, b) => (b[metric] as number) - (a[metric] as number));
    return videoLimit ? ranked.slice(0, videoLimit) : ranked;
  }, [threadAggs, metric, videoLimit]);
  const filteredThreads = useMemo(() => {
    const query = threadSearch.trim().toLowerCase();
    return query ? threadAggs.filter((a) => [a.key, threadTitles[a.key], ...a.users.map((id) => userNameMap[id] || id)].some((text) => text?.toLowerCase().includes(query))) : threadAggs;
  }, [threadAggs, threadSearch, threadTitles, userNameMap]);

  const threadTopSpec = useMemo((): VisualizationSpec => {
    const values = pageOf(rankedThreads, threadChartPage, AI_USAGE_CHART_SIZE)
      .map((a) => {
        const short = a.key.slice(0, 8);
        const title = titleOf(a.key);
        return {
          label: title ? `${title.slice(0, 26)}${title.length > 26 ? "…" : ""} · ${short}` : short,
          threadId: a.key,
          title: title || "-",
          value: a[metric],
        };
      });

    return {
      width: "container",
      height: Math.max(160, values.length * 28),
      config: VEGA_CONFIG,
      data: { values },
      mark: { type: "bar", tooltip: true, color: SINGLE_HUE, cornerRadiusEnd: 3, cursor: "pointer" },
      encoding: {
        y: { field: "threadId", type: "nominal", sort: "-x", axis: { title: null, labelLimit: 260, labelExpr: `(${JSON.stringify(Object.fromEntries(values.map((value) => [value.threadId, value.label])))})[datum.label]` } },
        x: { field: "value", type: "quantitative", axis: { title: metricLabel } },
        tooltip: [
          { field: "title", type: "nominal", title: "Video" },
          { field: "threadId", type: "nominal", title: "Thread" },
          { field: "value", type: "quantitative", title: metricLabel, format: valueFormat },
        ],
      },
    } as VisualizationSpec;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankedThreads, threadChartPage, metric, metricLabel, threadTitles]);

  // สัดส่วน call ต่อ provider (donut)
  const providerSpec = useMemo((): VisualizationSpec => {
    if (activeTab !== TAB.model) return {} as VisualizationSpec;
    // สี slot ถาวรจาก records (stable ข้าม filter) — legend/ค่า เฉพาะ provider ที่มีข้อมูลหลัง filter
    const provData = aggregateRecords(data, (r) => r.provider || "unknown");
    const callsOf = (prov: string) => provData.find((a) => a.key === prov)?.calls || 0;

    const present = allProviders
      .map((prov, i) => ({ provider: prov, calls: callsOf(prov), color: SERIES_COLORS[i % SERIES_COLORS.length] }))
      .filter((v) => v.calls > 0);

    const totalCalls = present.reduce((sum, v) => sum + v.calls, 0);
    const values = present.map((v) => {
      const p = totalCalls > 0 ? (v.calls / totalCalls) * 100 : 0;
      return { provider: v.provider, calls: v.calls, pct: p >= 10 ? `${Math.round(p)}%` : `${p.toFixed(1)}%` };
    });

    const domain = present.map((v) => v.provider);
    const range = present.map((v) => v.color);

    return {
      width: "container",
      height: 240,
      config: VEGA_CONFIG,
      data: { values },
      mark: { type: "arc", innerRadius: 55, tooltip: true, stroke: "white", strokeWidth: 2, cursor: "pointer" },
      encoding: {
        theta: { field: "calls", type: "quantitative" },
        color: { field: "provider", type: "nominal", title: "Provider", scale: { domain, range } },
        tooltip: [
          { field: "provider", type: "nominal", title: "Provider" },
          { field: "calls", type: "quantitative", title: "Calls (ครั้ง)", format: ",d" },
          { field: "pct", type: "nominal", title: "สัดส่วน" },
        ],
      },
    } as VisualizationSpec;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, allProviders, activeTab]);

  const handleProviderClick = (datum: any) => {
    if (!datum?.provider) return;
    const subset = data.filter((r) => (r.provider || "unknown") === datum.provider);
    setBreakdown({
      title: `Provider: ${datum.provider}`,
      subtitle: "ส่วนประกอบของชิ้น donut",
      records: subset,
    });
  };

  // ราย model ตาม metric ที่เลือก — แสดง "ทุก" model (feedback: wan2.7 video ต้องเห็น)
  const modelSpec = useMemo((): VisualizationSpec => {
    const ranked = [...modelAggs]
      .sort((a, b) => (b[metric] as number) - (a[metric] as number))
    const values = pageOf(ranked, modelChartPage, AI_USAGE_CHART_SIZE)
      .map((a) => {
        const [service, provider, model] = a.key.split("|");
        return { model: `${serviceLabelOf(service)} · ${provider} · ${model}`, modelKey: a.key, service, value: a[metric] };
      });

    return {
      width: "container",
      height: Math.max(160, values.length * 30 + 50),
      config: VEGA_CONFIG,
      data: { values },
      mark: { type: "bar", tooltip: true, color: SINGLE_HUE, cornerRadiusEnd: 3, cursor: "pointer" },
      encoding: {
        y: { field: "modelKey", type: "nominal", sort: "-x", axis: { title: null, labelLimit: 300, labelExpr: `(${JSON.stringify(Object.fromEntries(values.map((value) => [value.modelKey, value.model])))})[datum.label]` } },
        x: { field: "value", type: "quantitative", axis: { title: metricLabel } },
        tooltip: [
          { field: "model", type: "nominal", title: "Model" },
          { field: "value", type: "quantitative", title: metricLabel, format: valueFormat },
        ],
      },
    } as VisualizationSpec;
  }, [modelAggs, modelChartPage, metric, metricLabel]);

  const handleModelClick = (datum: any) => {
    if (!datum?.modelKey) return;
    const [service, , model] = String(datum.modelKey).split("|");
    const subset = data.filter((r) => usageModelKeyOf(r) === datum.modelKey);
    setBreakdown({
      title: model,
      subtitle: `service: ${serviceLabelOf(service)} · ส่วนประกอบของแท่ง ${metricLabel}`,
      records: subset,
    });
  };

  // ─── Renderers ─────────────────────────────────────────────────────────────

  // เซลล์ตาม registry เดียวกับ metricHeaders → หัว/เซลล์ตรงกันเสมอ (cost_usd format เป็นเงิน)
  const renderMetricCells = (a: AiUsageAggregate, result?: CostResult) => (
    <>
      {AI_USAGE_METRICS.map((m) => {
        const value = a[m.key] as number;
        if (m.key === "errors") {
          return (
            <td key={m.key} style={{ ...numTd, color: value ? "#dc2626" : "#94a3b8", fontWeight: value ? 700 : 400 }}>
              {value}
            </td>
          );
        }
        return (
          <td key={m.key} style={m.key === "cost_usd" ? { ...numTd, fontWeight: 600 } : numTd}>
            {m.key === "cost_usd" && result?.knownAllocationCount !== undefined ? <FfmpegAllocationCost result={result} /> : formatMetricValue(m.key, value)}
          </td>
        );
      })}
    </>
  );

  // หัวตารางมีหน่วยกำกับทุกคอลัมน์ (feedback: ตัวเลขต้องมีหน่วย)
  const metricHeaders = (
    <>
      {AI_USAGE_METRICS.map((m) => (
        <th key={m.key} style={{ ...thStyle, textAlign: "right" }}>{`${m.label} (${m.unit})`}</th>
      ))}
    </>
  );

  const filteredRecords = useMemo(() => {
    if (activeTab !== TAB.records) return [];
    const q = recordSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) =>
      [r.thread_id, r.thread_id ? threadTitles[r.thread_id] : "", r.user_id, r.user_id ? userNameMap[r.user_id] : "", r.service, serviceLabelOf(r.service), r.provider, r.model, usageModelOf(r), r.caller, r.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, recordSearch, threadTitles, userNameMap, activeTab]);

  // ── Breakdown ในการ์ดยอดรวม (feedback: ตัวเลขรวมลอย ๆ ไม่มีประโยชน์) ──
  type CardBreakdownRow = { label: string; value: number };
  type TotalCard = {
    label: string;
    value: number;
    unit: string;
    danger?: boolean;
    accent?: boolean;
    format?: (v: number) => string; // ไม่ระบุ = formatMetric
    note?: React.ReactNode; // ข้อความใต้ breakdown (เช่น เตือน model ที่ยังไม่ได้ตั้งราคา)
    breakdown: CardBreakdownRow[];
  };

  // แตกครบทุกรายการ: AI Calls แตกตาม service, ที่เหลือแตกตาม model
  const serviceBreakdowns = useMemo(() => aggregateRecords(data, (r) => r.service || "unknown", costOf), [data, costOf]);
  const modelBreakdowns = useMemo(() => aggregateRecords(data, (r) => usageModelOf(r) || "unknown", costOf), [data, costOf]);
  const buildBreakdown = (
    metricKey: AiUsageMetricKey,
    aggregates: AiUsageAggregate[],
    labelFn: (key: string) => string
  ): Pick<TotalCard, "breakdown"> => ({
    breakdown: aggregates
      .map((a) => ({ label: labelFn(a.key), value: a[metricKey] as number }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value),
  });

  const totalCards: TotalCard[] = useMemo(
    () => [
      { label: intl.formatMessage({ id: "megen.cost.allJobs" }), value: totals.calls, unit: "ครั้ง", ...buildBreakdown("calls", serviceBreakdowns, serviceLabelOf) },
      {
        label: "ค่าใช้จ่าย (ประมาณการ)",
        value: totals.cost_usd,
        unit: "USD",
        accent: true,
        format: formatMoney,
        ...buildBreakdown("cost_usd", modelBreakdowns, (k) => k),
        note: missingRateModels.length ? (
          <Typography
            id="megen-cost-missing-rate-note"
            onClick={() => setActiveTab(TAB.rates)}
            sx={{ mt: 0.75, fontSize: "0.72rem", color: "#b45309", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            ⚠ {missingRateModels.length} model ยังไม่ได้ตั้งราคา ({missingRateModels.slice(0, 3).join(", ")}
            {missingRateModels.length > 3 ? " …" : ""}) · ตั้งราคาที่แท็บ ราคา Model
          </Typography>
        ) : undefined,
      },
      { label: "รูปภาพ", value: totals.images, unit: "รูป", ...buildBreakdown("images", modelBreakdowns, (k) => k) },
      { label: "วิดีโอ", value: totals.video_seconds, unit: "วินาที", ...buildBreakdown("video_seconds", modelBreakdowns, (k) => k) },
      { label: "เสียง", value: totals.audio_seconds, unit: "วินาที", ...buildBreakdown("audio_seconds", modelBreakdowns, (k) => k) },
      { label: "Tokens เข้า", value: totals.input_tokens, unit: "tokens", ...buildBreakdown("input_tokens", modelBreakdowns, (k) => k) },
      { label: "Tokens ออก", value: totals.output_tokens, unit: "tokens", ...buildBreakdown("output_tokens", modelBreakdowns, (k) => k) },
      { label: "Errors", value: totals.errors, unit: "ครั้ง", danger: true, ...buildBreakdown("errors", modelBreakdowns, (k) => k) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totals, serviceBreakdowns, modelBreakdowns, missingRateModels, intl]
  );

  return (
    <Container id="megen-cost-root">
      <Inner>
      {/* Header: ช่วงเวลา */}
      <SectionCard sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "#1e293b", mr: 1 }}>
          MeGen Cost — การใช้งาน AI Cloud
        </Typography>

        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[7, 30, 90, 365].map((d) => (
            <Button key={d} size="small"
              variant={activePreset === d ? "contained" : "outlined"}
              onClick={() => handlePreset(d)}
              sx={{
                textTransform: "none", fontSize: "0.75rem", borderRadius: "9999px", py: 0.25, px: 1.5,
                ...(activePreset === d
                  ? { backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }
                  : { color: "#db2777", borderColor: "#f9a8d4", "&:hover": { borderColor: "#db2777", backgroundColor: "#fdf2f8" } }),
              }}>
              {intl.formatMessage({ id: "megen.cost.days" }, { days: d })}
            </Button>
          ))}
        </Box>

        <TextField type="date" size="small" value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
          inputProps={{ "aria-label": intl.formatMessage({ id: "megen.cost.start" }), style: { fontSize: "0.8rem", padding: "6px 8px" } }} />
        <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>ถึง</Typography>
        <TextField type="date" size="small" value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
          inputProps={{ "aria-label": intl.formatMessage({ id: "megen.cost.end" }), style: { fontSize: "0.8rem", padding: "6px 8px" } }} />

        <Button size="small" variant="contained" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={() => fetchRecords(startDate, endDate)}
          disabled={!!loading || !startDate || !endDate || startDate > endDate}
          sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "9999px", py: 0.25, px: 1.5, backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }}>
          โหลดข้อมูล
        </Button>

        {loading && <CircularProgress size={18} sx={{ color: "#db2777" }} />}
        {loading && <><Typography role="status" variant="caption">{intl.formatMessage({ id: "megen.cost.loading" }, { loaded: progress?.loaded || 0, total: progress?.total ?? intl.formatMessage({ id: "megen.cost.unknownTotal" }) })}</Typography><Button onClick={() => props.onEvent({ message: "HandleCancelAiUsageRecords", params: {} })}>{intl.formatMessage({ id: "megen.cost.cancel" })}</Button></>}
        {startDate > endDate && <Typography role="alert" color="error">{intl.formatMessage({ id: "megen.cost.invalidRange" })}</Typography>}

        {/* กรองข้อมูล 2 ชั้น — ใช้กับทุกการ์ด/กราฟ/ตารางในหน้า (flexBasis 100% = ขึ้นบรรทัดใหม่เสมอ) */}
        <Box sx={{ flexBasis: "100%", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>คน:</Typography>
            <Select size="small" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
              sx={{ fontSize: "0.9rem", minWidth: 170 }} id="megen-cost-filter-user">
              <MenuItem value="all" sx={{ fontSize: "0.9rem" }}>ทั้งหมด</MenuItem>
              {userOptions.map((uid) => (
                <MenuItem key={uid} value={uid} sx={{ fontSize: "0.9rem" }}>{nameOf(uid)}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>Cloud:</Typography>
            <Select size="small" value={cloudFilter} onChange={(e) => setCloudFilter(e.target.value)}
              sx={{ fontSize: "0.9rem", minWidth: 170 }} id="megen-cost-filter-cloud">
              <MenuItem value="all" sx={{ fontSize: "0.9rem" }}>ทั้งหมด</MenuItem>
              {cloudOptions.map((c) => (
                <MenuItem key={c.key} value={c.key} sx={{ fontSize: "0.9rem" }}>{c.label}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>{intl.formatMessage({ id: "megen.cost.projectName" })}:</Typography>
            <Select size="small" value={projectFilter} disabled={projectProgress?.loading}
              onChange={(event) => setProjectFilter(event.target.value)} inputProps={{ "aria-label": intl.formatMessage({ id: "megen.cost.projectFilter" }) }}
              sx={{ fontSize: "0.9rem", minWidth: 170 }} id="megen-cost-filter-project">
              <MenuItem value="all">{intl.formatMessage({ id: "megen.cost.all" })}</MenuItem>
              {projectOptions.map((option) => <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>)}
              {projectFilter !== "all" && !projectOptions.some((option) => option.key === projectFilter) && <MenuItem value={projectFilter}>{projectFilter}</MenuItem>}
            </Select>
          </Box>
        </Box>
      </SectionCard>

      {loadError && (
        <Box sx={{ mb: 1, p: 1.5, backgroundColor: "#fef3c7", border: "1px solid #fcd34d", borderRadius: "0.5rem" }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#b45309" }}>
            {intl.formatMessage({ id: loadError === "cancelled" ? "megen.cost.cancelled" : "megen.cost.incomplete" })}
          </Typography>
        </Box>
      )}
      {appliedRange && <Typography variant="caption" display="block" sx={{ mb: 1 }}>{intl.formatMessage({ id: "megen.cost.applied" }, { start: appliedRange.startDate, end: appliedRange.endDate, timezone: appliedRange.timezone, upper: appliedRange.endIso })}</Typography>}
      {titleProgress && titleProgress.resolved < titleProgress.total && <Box role="status" sx={{ mb: 1 }}><Typography variant="caption">{intl.formatMessage({ id: "megen.cost.titles" }, titleProgress)}</Typography>{!titleProgress.loading && <Button size="small" onClick={() => props.onEvent({ message: "HandleGetAiUsageTitles", params: {} })}>{intl.formatMessage({ id: "megen.cost.retryTitles" })}</Button>}</Box>}
      {projectFilter !== "all" && <Box role="status" sx={{ mb: 1 }}>
        <Typography variant="caption">{intl.formatMessage({ id: "megen.cost.projectActiveFilter" }, { project: projectOptions.find((option) => option.key === projectFilter)?.label || projectFilter })}</Typography>
        {projectProgress?.loading && <Typography variant="caption" display="block">{intl.formatMessage({ id: "megen.cost.projectLoading" }, { projects: projectProgress.projects, completed: projectProgress.completed, total: projectProgress.total ?? intl.formatMessage({ id: "megen.cost.unknownTotal" }) })}</Typography>}
        {(projectError || !projectIndex || !!projectIndex.failedProjects || !!projectIndex.conflictedThreads) && !projectProgress?.loading && <Typography role="alert" variant="caption" display="block" color="warning.main">{intl.formatMessage({ id: "megen.cost.projectIncomplete" }, { failed: projectIndex?.failedProjects || 0, conflicts: projectIndex?.conflictedThreads || 0 })}</Typography>}
      </Box>}

      {appliedRange && !projectFilterUnverified && <FfmpegUsageSummary records={data} costInfoOf={costInfoOf} />}

      {/* ยอดรวมทั้งหมด + breakdown ในการ์ด */}
      {appliedRange && !projectFilterUnverified && <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1, mb: 1 }}>
        {totalCards.map((c) => (
          <SectionCard key={c.label} sx={{ p: "0.75rem 1rem", mb: 0, display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", whiteSpace: "nowrap" }}>{c.label}</Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: c.danger && c.value > 0 ? "#dc2626" : c.accent ? "#be185d" : "#1e293b", fontVariantNumeric: "tabular-nums" }}>
                {(c.format || formatMetric)(c.value)}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>{c.unit}</Typography>
            </Box>
            {c.breakdown.length > 0 && (
              <Box sx={{ mt: 0.75, pt: 0.75, borderTop: "1px solid #f1f5f9" }}>
                {c.breakdown.map((b) => (
                  <Box key={b.label} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.125 }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={b.label}>
                      {b.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: c.danger ? "#dc2626" : "#334155", fontWeight: 600, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                      {(c.format || formatMetric)(b.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {c.note}
          </SectionCard>
        ))}
      </Box>}

      {/* ตัวพลิกข้อมูล */}
      <SectionCard sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, py: "0.75rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>Metric:</Typography>
          <Select size="small" value={metric} onChange={(e) => setMetric(e.target.value as AiUsageMetricKey)}
            sx={{ fontSize: "0.8rem", minWidth: 170 }}>
            {AI_USAGE_METRICS.map((m) => (
              <MenuItem key={m.key} value={m.key} sx={{ fontSize: "0.8rem" }}>{`${m.label} (${m.unit})`}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>แยกตาม:</Typography>
          <Select size="small" value={groupBy} onChange={(e) => setGroupBy(e.target.value as AiUsageGroupKey)}
            sx={{ fontSize: "0.8rem", minWidth: 120 }}>
            {AI_USAGE_GROUPS.map((g) => (
              <MenuItem key={g.key} value={g.key} sx={{ fontSize: "0.8rem" }}>{g.label}</MenuItem>
            ))}
          </Select>
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
          {records.length
            ? `${formatMetric(data.length)} จาก ${formatMetric(records.length)} รายการ · ${appliedRange?.startDate || ""} ถึง ${appliedRange?.endDate || ""} · คลิกแท่งกราฟเพื่อดูส่วนประกอบ`
            : ""}
        </Typography>
      </SectionCard>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ mb: 1, minHeight: 36, "& .MuiTab-root": { textTransform: "none", fontSize: "0.85rem", minHeight: 36, py: 0.5 }, "& .Mui-selected": { color: "#db2777 !important" }, "& .MuiTabs-indicator": { backgroundColor: "#db2777" } }}>
        <Tab value={TAB.daily} label="รายวัน" id="megen-cost-tab-daily" />
        <Tab value={TAB.project} label={intl.formatMessage({ id: "megen.cost.projectTab" })} id="megen-cost-tab-project" />
        <Tab value={TAB.thread} label="ราย Video/Thread" id="megen-cost-tab-thread" />
        <Tab value={TAB.model} label="ราย Model" id="megen-cost-tab-model" />
        <Tab value={TAB.records} label="Records" id="megen-cost-tab-records" />
        <Tab value={TAB.rates} label="ราคา Model" id="megen-cost-tab-rates" />
      </Tabs>

      {!loading && !loadError && data.length === 0 && activeTab !== TAB.rates && activeTab !== TAB.project && !projectFilterUnverified && (
        <SectionCard>
          <Typography sx={{ fontSize: "0.875rem", color: "#94a3b8", textAlign: "center", py: 4 }}>
            {records.length === 0
              ? "ไม่มีข้อมูลการใช้งานในช่วงเวลาที่เลือก"
              : "ไม่มีข้อมูลตาม filter ที่เลือก — ลองเปลี่ยนคน/Cloud หรือกลับเป็นทั้งหมด"}
          </Typography>
        </SectionCard>
      )}

      {activeTab === TAB.project && <CardAiUsageProjects records={data} index={projectIndex} progress={projectProgress} error={projectError}
        threadTitles={threadTitles} userNameMap={userNameMap} costInfoOf={costInfoOf}
        onRetry={() => props.onEvent({ message: "HandleGetAiUsageProjects", params: {} })}
        onRecords={(title, rows) => setBreakdown({ title, subtitle: intl.formatMessage({ id: "megen.cost.selectedLogs" }), records: rows })} />}

      {data.length > 0 && activeTab === TAB.daily && (
        <>
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: "0.75rem" }}>
              <SectionTitle sx={{ mb: 0 }}>{metricLabel} รายวัน แยกตาม {groupLabel} — คลิกแท่งเพื่อดูส่วนประกอบ</SectionTitle>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {([["stacked", "ซ้อนกัน"], ["grouped", "เทียบข้างกัน"]] as const).map(([mode, label]) => (
                  <Button key={mode} size="small"
                    variant={barMode === mode ? "contained" : "outlined"}
                    onClick={() => setBarMode(mode)}
                    sx={{
                      textTransform: "none", fontSize: "0.75rem", borderRadius: "9999px", py: 0.25, px: 1.5,
                      ...(barMode === mode
                        ? { backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }
                        : { color: "#db2777", borderColor: "#f9a8d4", "&:hover": { borderColor: "#db2777", backgroundColor: "#fdf2f8" } }),
                    }}>
                    {label}
                  </Button>
                ))}
              </Box>
            </Box>
            <CardMCVegaChart spec={dailySpec} onItemClick={handleDailyClick} />
          </SectionCard>
          <SectionCard>
            <SectionTitle>ตารางรายวัน</SectionTitle>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "860px" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>วันที่</th>
                    {metricHeaders}
                    <th style={{ ...thStyle, textAlign: "right" }}>Threads</th>
                  </tr>
                </thead>
                <tbody>
                  {[...dailyAggs].reverse().map((a) => (
                    <tr key={a.key}>
                      <td style={{ ...thTd, fontFamily: "monospace" }}>{a.key}</td>
                      {renderMetricCells(a)}
                      <td style={numTd}>{a.threads.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </SectionCard>
        </>
      )}

      {data.length > 0 && activeTab === TAB.thread && (
        <>
          <SectionCard>
            <SectionTitle>{intl.formatMessage({ id: "megen.cost.videoChart" }, { metric: metricLabel })}</SectionTitle>
            <Select size="small" value={videoLimit} onChange={(event) => setVideoLimit(Number(event.target.value))} inputProps={{ "aria-label": intl.formatMessage({ id: "megen.cost.chartScope" }) }}>{[0, 10, 25, 50].map((limit) => <MenuItem key={limit} value={limit}>{intl.formatMessage({ id: limit ? "megen.cost.top" : "megen.cost.all" }, { count: limit })}</MenuItem>)}</Select>
            <AiUsagePagination total={rankedThreads.length} page={threadChartPage} onChange={setThreadChartPage} size={AI_USAGE_CHART_SIZE} />
            <CardMCVegaChart spec={threadTopSpec} onItemClick={(d) => d?.threadId && setUsageThreadId(d.threadId)} />
          </SectionCard>
          <SectionCard>
            <SectionTitle>ทุก Video/Thread ในช่วงเวลา (คลิกแถวเพื่อดูรายละเอียด)</SectionTitle>
            <TextField size="small" label={intl.formatMessage({ id: "megen.cost.threadSearch" })} value={threadSearch} onChange={(event) => setThreadSearch(event.target.value)} />
            <AiUsagePagination total={filteredThreads.length} page={threadPage} onChange={setThreadPage} />
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "1080px" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Video / Thread</th>
                    <th style={thStyle}>Users</th>
                    {metricHeaders}
                    <th style={thStyle}>ใช้ครั้งแรก</th>
                    <th style={thStyle}>ใช้ล่าสุด</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOf(filteredThreads, threadPage).map((a) => (
                    <tr key={a.key} style={{ cursor: "pointer" }}
                      onClick={() => setUsageThreadId(a.key)}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fdf2f8")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}>
                      <td style={{ ...thTd, whiteSpace: "normal", minWidth: 220 }}>
                        <div style={{ fontWeight: 600, color: "#334155" }}>{titleOf(a.key) || "(ไม่มีชื่อ)"}</div>
                        <div style={{ fontFamily: "monospace", fontSize: "0.68rem", color: "#94a3b8" }}>
                          {a.key.slice(0, 8)}…
                          <Tooltip title="คัดลอก thread id">
                            <CopyIcon sx={{ fontSize: 12, ml: 0.5, color: "#cbd5e1", verticalAlign: "middle" }}
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(a.key); }} />
                          </Tooltip>
                        </div>
                      </td>
                      <td style={thTd}>{a.users.map(nameOf).join(", ") || "-"}</td>
                      {renderMetricCells(a)}
                      <td style={{ ...thTd, color: "#94a3b8" }}>{toLocalDateTime(a.first_used)}</td>
                      <td style={{ ...thTd, color: "#94a3b8" }}>{toLocalDateTime(a.last_used)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </SectionCard>
        </>
      )}

      {data.length > 0 && activeTab === TAB.model && (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
            <SectionCard sx={{ mb: 1 }}>
              <SectionTitle>สัดส่วน Calls (ครั้ง) ต่อ Provider — คลิกชิ้นเพื่อดูส่วนประกอบ</SectionTitle>
              <CardMCVegaChart spec={providerSpec} onItemClick={handleProviderClick} />
            </SectionCard>
            <SectionCard sx={{ mb: 1 }}>
              <SectionTitle>ทุก Model ตาม {metricLabel} — คลิกแท่งเพื่อดูส่วนประกอบ</SectionTitle>
              <AiUsagePagination total={modelAggs.length} page={modelChartPage} onChange={setModelChartPage} size={AI_USAGE_CHART_SIZE} />
              <CardMCVegaChart spec={modelSpec} onItemClick={handleModelClick} />
            </SectionCard>
          </Box>
          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: "0.75rem" }}>
              <Box>
                <SectionTitle sx={{ mb: 0.25 }}>ทุก Model ในช่วงเวลา</SectionTitle>
                <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                  แถวแยกตาม resolution/เสียงที่ผู้ให้บริการคิดเงิน · ราคาที่ใช้มาจาก price book และค่าที่แก้ในแท็บ ราคา Model (บันทึกใน browser นี้) ·
                  Local = ฟรี · GPU Hub คิดตามชั่วโมงเวลาเครื่อง (รวม call ที่ error) · cloud ไม่คิดเงินงานที่ error
                </Typography>
              </Box>
              <Typography
                id="megen-cost-model-edit-rates"
                onClick={() => setActiveTab(TAB.rates)}
                sx={{ fontSize: "0.75rem", color: "#db2777", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}
              >
                แก้ราคาที่แท็บ ราคา Model →
              </Typography>
            </Box>
            <AiUsagePagination total={priceLines.length} page={modelPage} onChange={setModelPage} />
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "1280px" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Service</th>
                    <th style={thStyle}>Provider</th>
                    <th style={thStyle}>Model</th>
                    <th style={thStyle}>ราคาต่อหน่วย (USD)</th>
                    {metricHeaders}
                  </tr>
                </thead>
                <tbody>
                  {pageOf(priceLines, modelPage).map((p) => {
                    const rate = p.info.result.rate;
                    const variantLabel = variantLabelOf(p.variant);
                    return (
                      <tr key={p.agg.key} style={{ backgroundColor: p.info.result.missingRate ? "#fffbeb" : undefined }}>
                        <td style={{ ...thTd, verticalAlign: "top" }}>{serviceLabelOf(p.service)}</td>
                        <td style={{ ...thTd, verticalAlign: "top" }}>{p.provider}</td>
                        <td style={{ ...thTd, fontFamily: "monospace", verticalAlign: "top" }}>
                          <AiUsageModelLabel provider={p.provider} model={p.model} />
                          {variantLabel && (
                            <span style={{ marginLeft: 6, fontSize: "0.68rem", color: "#94a3b8" }}>{variantLabel}</span>
                          )}
                          {rate.unit === "gpu_hour" && (
                            <div style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
                              {p.service === "ffmpeg" ? <FfmpegAllocationTime result={p.info.result} seconds={p.agg.process_seconds} /> : <>เวลาเครื่อง {formatMetric(p.agg.process_seconds)} วิ</>}
                            </div>
                          )}
                        </td>
                        <td style={{ ...thTd, whiteSpace: "normal", minWidth: 240, verticalAlign: "top" }}>
                          <AiRatePriceCell
                            idPrefix={`megen-cost-rate-${p.service}-${p.provider}-${p.model}-${p.variant}`}
                            rate={rate}
                            result={p.info.result}
                            fallbackFrom={p.info.fallbackFrom}
                            isOverridden={(v) => isRateOverridden(rate, v, rates.isOverridden)}
                            readOnlyHint={
                              p.info.unknownQuantityCalls > 0 ? (
                                <span style={{ fontSize: "0.68rem", color: "#64748b" }}>
                                  ไม่มีเวลาเครื่อง {p.info.unknownQuantityCalls}/{p.info.calls} call
                                </span>
                              ) : undefined
                            }
                          />
                        </td>
                        {renderMetricCells(p.agg, p.info.result)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </SectionCard>
        </>
      )}

      {data.length > 0 && activeTab === TAB.records && (
        <SectionCard>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <SectionTitle sx={{ mb: 0 }}>Records (ใหม่ → เก่า)</SectionTitle>
            <TextField size="small" label={intl.formatMessage({ id: "megen.cost.search" })}
              value={recordSearch} onChange={(e) => setRecordSearch(e.target.value)}
              inputProps={{ style: { fontSize: "0.8rem", padding: "6px 10px" } }} sx={{ minWidth: 280 }} />
            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
              {formatMetric(filteredRecords.length)} รายการ
            </Typography>
          </Box>
          <AiUsagePagination total={filteredRecords.length} page={recordPage} onChange={setRecordPage} />
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "1260px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>เวลา</th>
                  <th style={thStyle}>Video / Thread</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Service</th>
                  <th style={thStyle}>Model</th>
                  <th style={thStyle}>ปริมาณที่ใช้</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>{intl.formatMessage({ id: "megen.cost.recordCostCurrency" })}</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Process (วินาที)</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageOf(filteredRecords, recordPage).map((r: AiUsageRecordRow) => {
                  const { result: recordCost } = costInfoOf(r);
                  const costLabel = r.ffmpeg_task ? recordCost.knownAllocationCount
                    ? formatMoney(recordCost.cost)
                    : formatTaskCost(r.ffmpeg_task, intl.formatMessage({ id: "megen.cost.ffmpegCostUnknown" })) : r.status && r.status !== "success" && recordCost.cost === 0
                    ? "–"
                    : recordCost.missingRate
                      ? intl.formatMessage({ id: "megen.cost.missingPrice" })
                      : formatMoney(recordCost.cost);
                  return <tr key={r.id} style={{ backgroundColor: isUsageError(r) ? "#fef2f2" : undefined }}>
                    <td style={{ ...thTd, fontFamily: "monospace", color: "#64748b" }}>{toLocalDateTime(r.created_at)}</td>
                    <td style={{ ...thTd, cursor: r.thread_id ? "pointer" : "default" }}
                      onClick={() => r.thread_id && setUsageThreadId(r.thread_id)}>
                      {r.thread_id ? (
                        <Tooltip title={r.thread_id}>
                          <span style={{ color: "#db2777" }}>
                            {titleOf(r.thread_id)
                              ? `${titleOf(r.thread_id).slice(0, 24)}${titleOf(r.thread_id).length > 24 ? "…" : ""} `
                              : ""}
                            <span style={{ fontFamily: "monospace", fontSize: "0.68rem", color: "#94a3b8" }}>
                              {r.thread_id.slice(0, 8)}…
                            </span>
                          </span>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={thTd}>{nameOf(r.user_id)}</td>
                    <td style={thTd}>{serviceLabelOf(r.service)}</td>
                    <td style={{ ...thTd, fontFamily: "monospace" }}><AiUsageModelLabel provider={r.provider} model={r.model} videoDuration={r.service === "video" ? r.video_duration : undefined} /></td>
                    <td style={thTd}>
                      {[
                        r.ffmpeg_task && knownTaskValue(r.ffmpeg_task.allocated_instance_seconds) ? intl.formatMessage({ id: "megen.cost.ffmpegAllocated" }, { seconds: formatMetric(r.ffmpeg_task.allocated_instance_seconds) }) : "",
                        r.ffmpeg_task && knownTaskValue(r.ffmpeg_task.gpu_compute_seconds) ? intl.formatMessage({ id: "megen.cost.ffmpegCompute" }, { seconds: formatMetric(r.ffmpeg_task.gpu_compute_seconds) }) : "",
                        r.image_count ? `${r.image_count} รูป` : "",
                        r.video_duration ? `วิดีโอ ${formatMetric(r.video_duration)} วิ` : "",
                        r.audio_duration ? `เสียง ${formatMetric(r.audio_duration)} วิ` : "",
                        r.input_tokens || r.output_tokens ? `tokens ${formatMetric(r.input_tokens)}→${formatMetric(r.output_tokens)}` : "",
                        r.resolution || "",
                      ].filter(Boolean).join(" · ") || "-"}
                    </td>
                    <td style={{ ...numTd, fontWeight: 600 }}>{costLabel}</td>
                    <td style={numTd}>{r.duration_ms !== null ? formatMetric(r.duration_ms / 1000) : r.ffmpeg_task ? intl.formatMessage({ id: "megen.cost.ffmpegTimeUnknown" }) : "-"}</td>
                    <td style={{ ...thTd, color: isUsageError(r) ? "#dc2626" : r.status === "success" ? "#16a34a" : "#b45309", fontWeight: 600 }}>
                      {r.ffmpeg_task?.status || r.status}
                      {r.error_message && (
                        <Tooltip title={r.error_message}><span style={{ marginLeft: 4, cursor: "help" }}>ⓘ</span></Tooltip>
                      )}
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          </Box>
        </SectionCard>
      )}

      {/* แท็บ ราคา Model — แสดงเสมอ (ไม่ขึ้นกับข้อมูลในช่วงเวลา) และเป็นที่เดียวที่แก้ราคาได้ */}
      {activeTab === TAB.rates && (
        <CardAiRateCard records={data.filter((row) => !row.ffmpeg_task)} allRecords={records.filter((row) => !row.ffmpeg_task)} rangeLabel={appliedRange ? `${appliedRange.startDate} ถึง ${appliedRange.endDate}` : undefined} />
      )}

      {/* Drill-down: ส่วนประกอบของแท่ง/ชิ้นกราฟที่คลิก */}
      <ModAiUsageBreakdown
        open={!!breakdown}
        title={breakdown?.title || ""}
        subtitle={breakdown?.subtitle}
        records={breakdown?.records || []}
        userNameMap={userNameMap}
        threadTitles={threadTitles}
        onClose={() => setBreakdown(null)}
        onOpenThread={(tid) => setUsageThreadId(tid)}
      />

      {/* See-more modal (ตัวเดียวกับหน้า video gen) — render ทีหลัง breakdown เพื่อให้ซ้อนอยู่บนสุด */}
      <ModMeGenAiUsage
        dashboardRecords={selectedThreadRecords}
        open={!!usageThreadId}
        threadId={usageThreadId}
        threadTitle={titleOf(usageThreadId) || undefined}
        userNameMap={userNameMap}
        onClose={() => setUsageThreadId(null)}
        onFetchUsage={handleFetchAiUsageDetail}
        onEditRates={() => {
          setUsageThreadId(null);
          setActiveTab(TAB.rates);
        }}
      />
      </Inner>
    </Container>
  );
};

export default CardMeGenCostMain;
